import { isEmpty } from 'lodash';
import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { HashRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, StyledEngineProvider } from '@mui/material/styles';
import './index.css';
import routes from './Router';
import * as serviceWorker from './serviceWorker';
import userService from './user.service.js';
import useStore, { getCaregivers, getCareRecipients, checkAndUpdateTimezone } from './Store/Store';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ToastContainer } from 'react-toastify';
import Snackbar from './components/Snackbar.jsx';
import { onMessageListener } from './utils/firebaseNotificationConfigs.js';
import CompanionChat from './CanCare/Components/CompanionChat.jsx';

// MUI v5 theme configuration
const theme = createTheme({
  palette: {
    primary: {
      main: '#26786A',
    },
    secondary: {
      main: '#3f51b5',
    },
  },
});

// Suppress ResizeObserver loop error (benign warning from responsive components like charts)
const resizeObserverErr = (e) => {
  if (e.message === 'ResizeObserver loop completed with undelivered notifications.') {
    const resizeObserverErrDiv = document.getElementById('webpack-dev-server-client-overlay-div');
    const resizeObserverErrStyle = document.getElementById('webpack-dev-server-client-overlay');
    if (resizeObserverErrDiv) resizeObserverErrDiv.style.display = 'none';
    if (resizeObserverErrStyle) resizeObserverErrStyle.style.display = 'none';
    e.stopImmediatePropagation();
  }
};
window.addEventListener('error', resizeObserverErr);

const queryClient = new QueryClient();

const PrivateRoute = ({ Component }) => {
  const user = useStore((state) => state.user);
  const [state, setState] = React.useState({
    open: false,
    message: '',
    body: '',
  });

  useEffect(() => {
    onMessageListener()
      .then((payload) => {
        const { notification } = payload;
        setState({
          open: true,
          message: notification.title,
          body: notification.body,
        });
      })
      .catch((err) => console.error('Failed to setup notification listener:', err));

    return () => {};
  }, []);

  return (
    <>
      {user ? <Component /> : <Navigate to="/login" replace />}
      <Snackbar
        open={state.open}
        handleClose={() => setState({ open: false, message: '', body: '' })}
        variant="standard"
        message={state.message}
        body={state.body}
      />
    </>
  );
};

const routing = (
  <StyledEngineProvider injectFirst>
    <ThemeProvider theme={theme}>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
      <QueryClientProvider client={queryClient}>
        <Router>
          <Routes>
            {routes.map((route, i) => {
              if (route.private) {
                return <Route key={i} path={route.path} element={<PrivateRoute Component={route.component} />} />;
              }
              return <Route key={i} path={route.path} element={<route.component />} />;
            })}
          </Routes>
          <CompanionChat />
        </Router>
      </QueryClientProvider>
    </ThemeProvider>
  </StyledEngineProvider>
);

const authData = userService.getAuthData();

if (
  !isEmpty(authData.currentUserId) &&
  !isEmpty(authData.accessToken) &&
  authData.currentUserId !== 'null' &&
  authData.accessToken !== 'null'
) {
  fetch(`${process.env.REACT_APP_EXPRESS_URL}/api/users/${authData.currentUserId}/userInfo`, {
    method: 'GET',
    headers: {
      authorization: authData.accessToken + '~123123',
      'ngrok-skip-browser-warning': 'true',
    },
  })
    .then((res) => res.json())
    .then(async ({ data: userData }) => {
      if (!userData.error) {
        useStore.getState().updateUser(userData);
        userService.updateCurrentUser(userData);
        // ensure token already exists in storage before timezone update in app boot
        await checkAndUpdateTimezone();

        await getCareRecipients({ userId: userData?._id });
        await getCaregivers({ userId: userData?._id });
      }

      ReactDOM.render(routing, document.getElementById('root'));
    })
    .catch((err) => {
      console.log(err);
    });
} else {
  ReactDOM.render(routing, document.getElementById('root'));
}

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
